import { memo } from 'react';
import { attachIcon, drivingModeIcon, micIcon, micOffIcon, sendIcon, stopIcon } from '../icons';
import type { AuthState, FileAttachment, FileUploadState } from '../../types';
import { getPlaceholder, RECORDING_WAVE_BAR_COUNT } from './helpers';
import { AttachmentChips } from './AttachmentChips';
import { t } from '../../i18n';

interface ChatComposerProps {
  fileUpload: FileUploadState;
  pendingAttachments: FileAttachment[];
  removePendingAttachment(fileId: string): void;
  showAttach: boolean;
  showMic: boolean;
  showDrivingMode: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  authStatus: AuthState['status'];
  inputDraft: string;
  inputRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  drivingListening: boolean;
  waveBarsContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  onAttachClick(): void;
  onMicClick(): void;
  onDrivingModeClick(): void;
  onTextareaInput(e: React.FormEvent<HTMLTextAreaElement>): void;
  onInputChange(e: React.ChangeEvent<HTMLInputElement>): void;
  onKeyDown(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  onSubmit(): void;
  onStop(): void;
}

interface InputBarLeadingButtonsProps {
  showAttach: boolean;
  showMic: boolean;
  showDrivingMode: boolean;
  isLoading: boolean;
  drivingListening: boolean;
  onAttachClick(): void;
  onMicClick(): void;
  onDrivingModeClick(): void;
}

interface SendButtonProps {
  isLoading: boolean;
  onSubmit(): void;
  onStop(): void;
}

const InputBarLeadingButtons = memo(function InputBarLeadingButtons({
  showAttach,
  showMic,
  showDrivingMode,
  isLoading,
  drivingListening,
  onAttachClick,
  onMicClick,
  onDrivingModeClick,
}: InputBarLeadingButtonsProps) {
  return (
    <>
      {showAttach && (
        <button
          type="button"
          className="attach-btn"
          aria-label={t('attachFile')}
          disabled={isLoading}
          onClick={onAttachClick}
          dangerouslySetInnerHTML={{ __html: attachIcon }}
        />
      )}

      {showMic && (
        <button
          type="button"
          className={`mic-btn${drivingListening ? ' mic-active' : ''}`}
          aria-label={t('voiceInput')}
          disabled={isLoading}
          onClick={onMicClick}
          dangerouslySetInnerHTML={{ __html: drivingListening ? micOffIcon : micIcon }}
        />
      )}

      {showDrivingMode && (
        <button
          type="button"
          className="driving-mode-btn"
          aria-label={t('drivingMode')}
          title={t('drivingMode')}
          onClick={onDrivingModeClick}
          dangerouslySetInnerHTML={{ __html: drivingModeIcon }}
        />
      )}
    </>
  );
});

const InputBarActionButton = memo(function InputBarActionButton({
  isLoading,
  onSubmit,
  onStop,
}: SendButtonProps) {
  if (isLoading) {
    return (
      <button
        type="button"
        className="send-btn"
        aria-label={t('stop')}
        onClick={onStop}
        dangerouslySetInnerHTML={{ __html: stopIcon }}
      />
    );
  }
  return (
    <button
      type="button"
      className="send-btn"
      aria-label={t('send')}
      onClick={onSubmit}
      dangerouslySetInnerHTML={{ __html: sendIcon }}
    />
  );
});

export function ChatComposer({
  fileUpload,
  pendingAttachments,
  removePendingAttachment,
  showAttach,
  showMic,
  showDrivingMode,
  isLoading,
  isAuthenticated,
  authStatus,
  inputDraft,
  inputRef,
  drivingListening,
  waveBarsContainerRef,
  onAttachClick,
  onMicClick,
  onDrivingModeClick,
  onTextareaInput,
  onInputChange,
  onKeyDown,
  onSubmit,
  onStop,
}: ChatComposerProps) {
  return (
    <>
      {fileUpload.uploading && (
        <div className="upload-progress">
          <span className="upload-spinner" />
          {t('uploading')}
        </div>
      )}

      <AttachmentChips
        attachments={pendingAttachments}
        onRemove={removePendingAttachment}
      />

      {fileUpload.error && (
        <div className="upload-inline-error" role="alert">
          {fileUpload.error}
        </div>
      )}

      <div className="input-bar">
        <InputBarLeadingButtons
          showAttach={showAttach}
          showMic={showMic}
          showDrivingMode={showDrivingMode}
          isLoading={isLoading}
          drivingListening={drivingListening}
          onAttachClick={onAttachClick}
          onMicClick={onMicClick}
          onDrivingModeClick={onDrivingModeClick}
        />

        <div className={`chat-input-shell${drivingListening ? ' chat-input-shell-recording' : ''}`}>
          {isAuthenticated ? (
            <textarea
              ref={(node) => {
                inputRef.current = node;
              }}
              className="chat-input"
              rows={1}
              placeholder={getPlaceholder(authStatus)}
              autoComplete="off"
              value={inputDraft}
              onInput={onTextareaInput}
              onChange={() => { /* controlled by onInput */ }}
              onKeyDown={onKeyDown}
            />
          ) : (
            <input
              ref={(node) => {
                inputRef.current = node;
              }}
              type={authStatus === 'unauthenticated' ? 'email' : 'text'}
              className="chat-input"
              value={inputDraft}
              placeholder={getPlaceholder(authStatus)}
              inputMode={authStatus === 'otp_pending' ? 'numeric' : undefined}
              maxLength={authStatus === 'otp_pending' ? 6 : undefined}
              autoComplete={
                authStatus === 'unauthenticated'
                  ? 'email'
                  : authStatus === 'otp_pending'
                  ? 'one-time-code'
                  : 'off'
              }
              onChange={onInputChange}
              onKeyDown={onKeyDown}
            />
          )}

          {drivingListening && (
            <>
              <div className="recording-wave-overlay" aria-hidden="true" />
              <div
                ref={(node) => {
                  waveBarsContainerRef.current = node;
                }}
                className="recording-wave"
                aria-hidden="true"
              >
                {Array.from({ length: RECORDING_WAVE_BAR_COUNT }, (_, i) => (
                  <span
                    key={i}
                    className="recording-wave-bar"
                    style={{ '--wave-index': i, '--wave-scale': '0.18' } as React.CSSProperties}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <InputBarActionButton
          isLoading={isLoading}
          onSubmit={onSubmit}
          onStop={onStop}
        />
      </div>
    </>
  );
}
